import { useState } from "react";
import { Download, Mail, Copy, Check } from "lucide-react";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [key, setKey] = useState("");
  const [subscribers, setSubscribers] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/newsletter/subscribers?key=${key}`);
      if (!res.ok) throw new Error("Invalid key");
      const data = await res.json();
      setSubscribers(data.subscribers);
      setCount(data.count);
      setAuthed(true);
    } catch {
      setError("Invalid admin key");
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/newsletter/subscribers?key=${key}`);
      const data = await res.json();
      setSubscribers(data.subscribers);
      setCount(data.count);
    } catch {} finally { setLoading(false); }
  };

  const copyAllEmails = () => {
    const emails = subscribers.map((s) => s.email).join(", ");
    navigator.clipboard.writeText(emails);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const exportCSV = () => {
    const header = "Email,Subscribed At\n";
    const rows = subscribers.map((s) => `${s.email},${s.subscribed_at}`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "padlr-subscribers.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <main className="min-h-screen bg-[#FDFDFD] flex items-center justify-center px-4">
        <SEO title="Admin" path="/admin" />
        <div className="w-full max-w-sm">
          <h1 className="text-2xl tracking-tighter text-[#0A0A0A] mb-2 text-center">Admin</h1>
          <p className="text-xs text-[#888] text-center mb-8" style={{ fontStyle: "normal" }}>Enter your admin key to continue</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Admin Key"
              data-testid="admin-key-input"
              className="w-full border border-[#E5E5E5] px-4 py-3 text-sm text-[#0A0A0A] placeholder:text-[#aaa] focus:outline-none focus:border-[#0A0A0A] transition-colors"
              style={{ fontStyle: "normal" }}
            />
            {error && <p className="text-xs text-[#D9381E]" style={{ fontStyle: "normal" }}>{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="w-full bg-[#0A0A0A] text-white py-3 text-xs tracking-[0.2em] uppercase hover:bg-[#1a1a1a] transition-colors disabled:opacity-50"
              style={{ fontStyle: "normal" }}
            >
              {loading ? "..." : "Access"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#FDFDFD]">
      <SEO title="Admin — Subscribers" path="/admin" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl tracking-tighter text-[#0A0A0A]">Subscribers</h1>
            <p className="text-sm text-[#888] mt-1" style={{ fontStyle: "normal" }}>{count} total</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={copyAllEmails}
              data-testid="copy-emails-button"
              className="flex items-center gap-2 px-4 py-2 border border-[#E5E5E5] text-xs tracking-[0.1em] uppercase hover:border-[#0A0A0A] transition-colors"
              style={{ fontStyle: "normal" }}
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied" : "Copy All"}
            </button>
            <button
              onClick={exportCSV}
              data-testid="export-csv-button"
              className="flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-xs tracking-[0.1em] uppercase hover:bg-[#1a1a1a] transition-colors"
              style={{ fontStyle: "normal" }}
            >
              <Download className="w-3.5 h-3.5" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Mailto all link */}
        <a
          href={`mailto:info@padlr.co?bcc=${subscribers.map((s) => s.email).join(",")}`}
          data-testid="email-all-button"
          className="inline-flex items-center gap-2 mb-8 text-xs tracking-[0.1em] uppercase text-[#8B6F47] border-b border-[#8B6F47] pb-0.5 hover:opacity-70 transition-opacity"
          style={{ fontStyle: "normal" }}
        >
          <Mail className="w-3.5 h-3.5" />
          Email All Subscribers
        </a>

        {/* Table */}
        <div className="border border-[#E5E5E5]">
          <div className="grid grid-cols-[1fr_auto] bg-[#F5F5F5] px-4 py-3 text-xs tracking-[0.1em] uppercase text-[#888]" style={{ fontStyle: "normal" }}>
            <span>Email</span>
            <span>Date</span>
          </div>
          {subscribers.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-[#888]">No subscribers yet</div>
          ) : (
            subscribers.map((sub, i) => (
              <div
                key={sub.email}
                data-testid="subscriber-row"
                className={`grid grid-cols-[1fr_auto] px-4 py-3 text-sm ${
                  i % 2 === 0 ? "bg-white" : "bg-[#FAFAFA]"
                }`}
                style={{ fontStyle: "normal" }}
              >
                <span className="text-[#0A0A0A]">{sub.email}</span>
                <span className="text-[#888] text-xs">
                  {sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString() : "—"}
                </span>
              </div>
            ))
          )}
        </div>

        <button
          onClick={refresh}
          className="mt-6 text-xs text-[#888] tracking-wide hover:text-[#0A0A0A] transition-colors"
          style={{ fontStyle: "normal" }}
        >
          {loading ? "Refreshing..." : "Refresh list"}
        </button>
      </div>
    </main>
  );
}
